/**
 * R60 · ResearchDialog — one-click AI research on a Prospect / Lead / Client.
 *
 * Backed by /api/v1/workspaces/{ws}/research/{kind} which calls Claude
 * Sonnet with all fields we know about the target and returns a
 * structured JSON briefing. Also shows past research runs so agents can
 * re-open cached results without spending another LLM call.
 */
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, Loader2, X, ChevronDown, ChevronRight } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";

const KIND_LABEL = {
  prospect: "Prospect",
  lead: "Lead",
  company: "Company",
};

export default function ResearchDialog({ open, onClose, kind, targetId, targetName }) {
  const { activeWorkspaceId } = useAuth();
  const [focus, setFocus] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!open || !activeWorkspaceId || !targetId) return;
    api.get(`/workspaces/${activeWorkspaceId}/research/history/${kind}/${targetId}`)
      .then((r) => setHistory(r.data?.rows || []))
      .catch(() => setHistory([]));
  }, [open, activeWorkspaceId, kind, targetId]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/workspaces/${activeWorkspaceId}/research/${kind}`,
        { id: targetId, focus: focus.trim() || null },
      );
      setResult(r.data);
      setHistory([r.data, ...history]);
      toast.success("Research complete");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Research failed"));
    } finally { setBusy(false); }
  };

  const shown = result?.result || null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-none"
                      data-testid="research-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Research {KIND_LABEL[kind] || kind} · <span className="text-muted-foreground font-normal truncate">{targetName || targetId}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="label-eyebrow mb-1 inline-block text-[10px]">Optional focus (leave blank for full briefing)</label>
              <Input value={focus} onChange={(e) => setFocus(e.target.value)}
                    placeholder='e.g. "their pricing model" or "recent product launches"'
                    className="h-9 rounded-none"
                    data-testid="research-focus" />
            </div>
            <Button onClick={run} disabled={busy || !targetId}
                    className="rounded-none h-9"
                    data-testid="research-run">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Researching…</>
                    : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Run research</>}
            </Button>
          </div>

          {shown && <ResultPanel result={shown} kind={kind} />}

          {history.length > 0 && (
            <div className="border border-border">
              <button type="button"
                onClick={() => setHistoryOpen((s) => !s)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-muted/40 text-left"
                data-testid="research-history-toggle">
                {historyOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <span className="label-eyebrow text-[10px]">Past research ({history.length})</span>
              </button>
              {historyOpen && (
                <div className="divide-y divide-border">
                  {history.map((h) => (
                    <button key={h.id} type="button"
                      onClick={() => setResult(h)}
                      className="w-full text-left px-3 py-2 hover:bg-muted/30 text-xs"
                      data-testid={`research-history-item-${h.id}`}>
                      <div className="font-medium">{h.created_by_name || "unknown"} · {fmtRelative(h.created_at)}</div>
                      {h.input_context?.focus && (
                        <div className="text-[10px] text-muted-foreground truncate">Focus: {h.input_context.focus}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-none"
                  data-testid="research-close">
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResultPanel({ result, kind }) {
  if (result.raw_text) {
    // Model returned non-JSON — show raw text with a warning
    return (
      <div className="border border-amber-400 bg-amber-50 p-3">
        <div className="text-[10px] font-semibold text-amber-800 mb-1">
          Model returned unstructured output. Raw text:
        </div>
        <pre className="whitespace-pre-wrap text-xs text-amber-900">{result.raw_text}</pre>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {result.summary && (
        <Section label="Summary" testid="research-section-summary">
          <p className="text-sm">{result.summary}</p>
        </Section>
      )}
      {/* R64.11 · Contact hints — likely emails / phone / socials that AI
           inferred from the lead's context. Rendered EARLY (right under
           summary) so users spotting outreach targets don't have to
           scroll. Yellow chip warns these are pattern-based guesses. */}
      {result.contact_hints && (
        result.contact_hints.likely_emails?.length ||
        result.contact_hints.known_email ||
        result.contact_hints.phone_or_mobile ||
        result.contact_hints.linkedin_url
      ) && (
        <Section label="Contact hints (verify before use)" testid="research-section-contact-hints">
          <div className="space-y-1 text-sm">
            {result.contact_hints.known_email && (
              <div><span className="text-[10px] uppercase tracking-widest text-emerald-700 mr-1">Verified email</span>
                <a href={`mailto:${result.contact_hints.known_email}`} className="text-primary hover:underline">{result.contact_hints.known_email}</a></div>
            )}
            {result.contact_hints.likely_emails?.length > 0 && (
              <div>
                <span className="text-[10px] uppercase tracking-widest text-amber-700 mr-1">Likely email(s) — verify</span>
                <ul className="ml-4 list-disc">
                  {result.contact_hints.likely_emails.map((e, i) => (
                    <li key={i} data-testid={`research-likely-email-${i}`}>
                      <a href={`mailto:${e}`} className="text-primary hover:underline">{e}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.contact_hints.phone_or_mobile && (
              <div><span className="text-[10px] uppercase tracking-widest text-emerald-700 mr-1">Phone</span>
                <a href={`tel:${result.contact_hints.phone_or_mobile}`} className="text-primary hover:underline font-mono">{result.contact_hints.phone_or_mobile}</a></div>
            )}
            {result.contact_hints.linkedin_url && (
              <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">LinkedIn</span>
                <a href={result.contact_hints.linkedin_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{result.contact_hints.linkedin_url}</a></div>
            )}
            {result.contact_hints.twitter_url && (
              <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Twitter</span>
                <a href={result.contact_hints.twitter_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">{result.contact_hints.twitter_url}</a></div>
            )}
            {result.contact_hints.personal_website && (
              <div><span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Website</span>
                <a href={result.contact_hints.personal_website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{result.contact_hints.personal_website}</a></div>
            )}
            {result.contact_hints.notes && (
              <div className="text-[11px] text-muted-foreground italic mt-1">{result.contact_hints.notes}</div>
            )}
          </div>
        </Section>
      )}
      {result.interpretation && (
        <Section label="Query interpretation">
          <p className="text-sm">{result.interpretation}</p>
        </Section>
      )}
      {result.buying_signals?.length > 0 && (
        <Section label="Buying signals" testid="research-section-signals">
          <BulletList items={result.buying_signals} />
        </Section>
      )}
      {result.pain_points?.length > 0 && (
        <Section label="Likely pain points">
          <BulletList items={result.pain_points} />
        </Section>
      )}
      {result.conversation_openers?.length > 0 && (
        <Section label="Conversation openers">
          <BulletList items={result.conversation_openers} numbered />
        </Section>
      )}
      {result.questions_to_ask?.length > 0 && (
        <Section label="Questions to ask">
          <BulletList items={result.questions_to_ask} />
        </Section>
      )}
      {result.risks_to_avoid?.length > 0 && (
        <Section label="Risks to avoid">
          <BulletList items={result.risks_to_avoid} />
        </Section>
      )}

      {/* Company-specific sections */}
      {result.recent_news?.length > 0 && (
        <Section label="Recent news (verify)" testid="research-section-news">
          <div className="space-y-1">
            {result.recent_news.map((n, i) => (
              <div key={i} className="text-xs border-l-2 border-primary/40 pl-2">
                <div className="font-medium">{n.headline}</div>
                <div className="text-[10px] text-muted-foreground">{n.date_approx} · {n.source_hint}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {result.funding_events?.length > 0 && (
        <Section label="Funding events">
          <BulletList items={result.funding_events.map((f) => `${f.stage} · ${f.amount} · ${f.when}`)} />
        </Section>
      )}
      {result.hiring_signals?.length > 0 && (
        <Section label="Hiring signals">
          <BulletList items={result.hiring_signals} />
        </Section>
      )}
      {result.leadership_changes?.length > 0 && (
        <Section label="Leadership changes">
          <BulletList items={result.leadership_changes} />
        </Section>
      )}
      {result.product_launches?.length > 0 && (
        <Section label="Product launches">
          <BulletList items={result.product_launches} />
        </Section>
      )}
      {result.pain_hypotheses?.length > 0 && (
        <Section label="Pain hypotheses">
          <BulletList items={result.pain_hypotheses} />
        </Section>
      )}
      {result.abm_plays?.length > 0 && (
        <Section label="ABM plays">
          <BulletList items={result.abm_plays} numbered />
        </Section>
      )}

      {/* Discovery */}
      {result.search_hints?.length > 0 && (
        <Section label="Suggested Apollo / LinkedIn filters">
          <BulletList items={result.search_hints} />
        </Section>
      )}
      {result.candidate_people?.length > 0 && (
        <Section label="Candidate personas">
          <div className="space-y-2">
            {result.candidate_people.map((p, i) => (
              <div key={i} className="border border-border p-2">
                <div className="font-medium text-sm">{p.title} · {p.seniority}</div>
                <div className="text-[10px] text-muted-foreground">{p.industry_fit}</div>
                <div className="text-xs mt-1">{p.why}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
      {result.candidate_companies?.length > 0 && (
        <Section label="Candidate companies">
          <div className="space-y-2">
            {result.candidate_companies.map((c, i) => (
              <div key={i} className="border border-border p-2">
                <div className="font-medium text-sm">{c.name_hint}</div>
                <div className="text-[10px] text-muted-foreground">{c.industry} · {c.region}</div>
                <div className="text-xs mt-1">{c.why}</div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {result.recommended_next_step && (
        <div className="border-2 border-primary/50 bg-primary/5 p-3">
          <div className="label-eyebrow text-[10px] text-primary mb-1">Recommended next step</div>
          <div className="text-sm font-medium">{result.recommended_next_step}</div>
        </div>
      )}
      <div className="text-[10px] text-muted-foreground italic pt-2 border-t border-border">
        Generated by Claude Sonnet 4.6 · Some signals may need verification with live sources.
        {kind === "company" && " For live news/funding, integrate a web-search API (Serper / Perplexity)."}
      </div>
    </div>
  );
}


function Section({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow text-[10px] mb-1">{label}</div>
      {children}
    </div>
  );
}


function BulletList({ items, numbered }) {
  if (numbered) {
    return (
      <ol className="list-decimal ml-5 space-y-0.5 text-sm">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ol>
    );
  }
  return (
    <ul className="list-disc ml-5 space-y-0.5 text-sm">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}
